import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import debounce from 'lodash/debounce';

import { AppContext } from '@prisjakt/ui-app-context';

import T from '@prisjakt/ui-translate';

import {
  Info,
  MultiRadio,
  Text,
  TextTypes,
  Title,
  Variants,
  ActionType,
} from '@prisjakt/ui-review-form';

import * as S from './styles';

import {
  closeReviewDialog,
  createShopReview,
  deleteShopReview,
  editShopReview,
  saveReviewDraft,
} from '../../actions';
import { fetchShopReview } from '../../../../globals/api/requests';
import { ShopReviewType, trackStoreReviewFormViewed } from '../../tracking';

import { Questions, Review as ReviewType, Store } from '../../types';
import { Answer } from '../../../../types';
import { AppState } from '../../../../reducers';
import StoreInfoCard from '../store-info-card';
import { useIsAuthenticated } from '../../../../hooks/use-is-authenticated';
import { useReviewErrorToast } from '../../hooks/use-review-error-toast';
import { SHOP_LOGO_WIDTH } from '../../../store/constants';

type ReviewState =
  | (ReviewType & {
      id?: string;
      answers?: Answer[];
    })
  | undefined
  | null;

type Props = {
  uid: string;
  store: Store;
  review?: ReviewType & { code?: string };
};

const FORM_ID = 'shopReview';

const selectOpenId = (state: AppState) => state.areas.review.dialog.openId;
const selectPending = (state: AppState) => state.areas.review.pendingRequest;
const selectDisplayName = (state: AppState) =>
  state.areas.account.me ? !state.areas.account.me.displayName : null;
const selectRequestError = (state: AppState) => state.areas.review.requestError;
const selectDrafts = (state: AppState) => state.areas.review.drafts;

const StoreReviewForm = ({ uid, store, review }: Props) => {
  const { t, market, linkTo } = useContext(AppContext);
  const dispatch = useDispatch();
  const { push } = useHistory();
  const location = useLocation();
  const openId = useSelector(selectOpenId);
  const pending = useSelector(selectPending);
  const isAuthenticated = useIsAuthenticated();
  const setDisplayName = useSelector(selectDisplayName);
  const dispatchReviewErrorToast = useReviewErrorToast();
  const requestError = useSelector(selectRequestError);
  const drafts = useSelector(selectDrafts);
  const [viewportHeight, setViewportHeight] = useState<string>('100vh');

  useEffect(() => {
    if (requestError) {
      dispatchReviewErrorToast(requestError);
    }
  }, [requestError]);

  const shouldSave = useRef(true);
  const shouldFetch = useRef(true);

  const [currentReview, setCurrentReview] = useState<ReviewState>(review);

  const getActionType = useCallback((): ActionType => {
    if (review) {
      return ActionType.EDIT;
    }

    if (isAuthenticated) {
      return ActionType.PUBLISH;
    }

    return ActionType.CREATE;
  }, [isAuthenticated, review]);

  const isEdit = getActionType() === ActionType.EDIT;

  const setPurchaseDate = Boolean(
    market.modules?.userReviewPurchaseDate?.enabled
  );

  const redirectToShop = useCallback(
    (query?: { [key: string]: string }) => {
      push(linkTo('store', { id: store.id, pathName: store.pathName }, query));
    },
    [linkTo, push, store.id]
  );

  useEffect(() => {
    // In case the initial render is missing `review`, we update the state when it appears
    // For example when a user lands on then `?review=write` page but already has a review
    // the prop `review` will be missing but added on a re-render.
    if (review && shouldFetch.current) setCurrentReview(review);
  }, [review]);

  useEffect(() => {
    // Fetch all custom questions and answers for a review on mount
    // or when `actionType` changes
    if (isEdit && shouldFetch.current) {
      const reviewId = review?.id;

      fetchShopReview(reviewId).then(response => {
        if (response.errors?.length > 0 && !currentReview) {
          handleClose();
          redirectToShop({ notification: 'error' });
        }

        if (response.data && !response.errors?.length) {
          setCurrentReview(response.data.shopReview);
        }

        shouldFetch.current = false;
      });
    }
  }, []);

  useEffect(() => {
    // Fill form using saved draft data if it exists.
    if (review?.id) {
      if (drafts && drafts[review.id]) {
        // @ts-ignore
        setCurrentReview(mapQuestionsToReview(drafts[review.id]));
      }
    } else if (drafts && drafts[store.id]) {
      // @ts-ignore
      setCurrentReview(mapQuestionsToReview(drafts[store.id]));
    }

    return () => {
      // Prevents `saveDraft` from saving empty value if form is closed before save occurs.
      shouldSave.current = false;
    };
  }, []);

  useEffect(() => {
    const updateViewportHeight = () => {
      if (!IS_BROWSER) return;

      // On mobile `vh` does not take browser UI into consideration,
      // causing 100vh to be higher than what is visible.
      // `window.innerHeight` is the actual visible height of the window
      // and will be updated every render.
      setViewportHeight(`${window.innerHeight}px`);
    };

    window.addEventListener('resize', updateViewportHeight);
    updateViewportHeight();

    return () => window.removeEventListener('resize', updateViewportHeight);
  }, [setViewportHeight]);

  useEffect(() => {
    trackStoreReviewFormViewed({
      type: ShopReviewType.SHOP,
      storeId: store.id,
      storeName: store.name,
    });
  }, []);

  const handleClose = useCallback(() => {
    dispatch(closeReviewDialog());
  }, [dispatch]);

  const mapQuestionsToReview = (questions: { [key in Questions]: string }) => {
    const {
      [Questions.Rating]: rating,
      [Questions.Review]: reviewText,
      [Questions.Email]: email,
      [Questions.DisplayName]: displayName,
      [Questions.PurchaseDate]: purchaseDate,
      ...customQuestions
    } = questions;

    let scaledRating;
    if (rating) scaledRating = Number(rating) * 2;

    const answers = Object.keys(customQuestions).map(question => ({
      id: question,
      answer: Number(customQuestions[question]),
    }));

    return {
      rating: scaledRating,
      reviewText,
      email,
      displayName,
      purchaseDate,
      answers,
    };
  };

  const handleSubmit = useCallback(
    formData => {
      const {
        rating,
        reviewText,
        email,
        displayName,
        purchaseDate,
        answers,
      } = mapQuestionsToReview(formData);

      switch (getActionType()) {
        case ActionType.PUBLISH:
        case ActionType.CREATE: {
          dispatch(
            createShopReview({
              belongsToId: store.id,
              belongsToName: store.name,
              rating,
              reviewText,
              purchaseDate,
              answers,
              email,
              displayName,
            })
          );
          break;
        }

        case ActionType.EDIT: {
          dispatch(
            editShopReview({
              belongsToId: store.id,
              belongsToName: store.name,
              reviewId: currentReview?.id,
              rating,
              reviewText,
              purchaseDate,
              answers,
              token: review?.code,
              location: location.pathname,
            })
          );
          break;
        }

        default:
      }
    },
    [
      getActionType,
      dispatch,
      store.id,
      store.name,
      currentReview,
      review,
      location.pathname,
    ]
  );

  const handleDelete = useCallback(() => {
    dispatch(
      deleteShopReview({
        reviewId: currentReview?.id,
        token: review?.code,
        shopId: store.id,
        shopName: store.name,
        location: location.pathname,
      })
    );
  }, [
    dispatch,
    currentReview,
    review,
    store.id,
    store.name,
    location.pathname,
  ]);

  const saveDraft = useCallback(
    debounce(formData => {
      if (shouldSave.current) {
        if (
          getActionType() === ActionType.PUBLISH ||
          getActionType() === ActionType.CREATE
        ) {
          dispatch(saveReviewDraft({ [store.id]: formData }));
        } else if (review?.id) {
          dispatch(saveReviewDraft({ [review.id]: formData }));
        }
      }
    }, 300),
    [dispatch, store.id]
  );

  const questions = [
    { id: Questions.DisplayName },
    { id: Questions.Rating },
    { id: Questions.Review },
    { id: Questions.Recommend },
    { id: Questions.DeliveryAccuracy },
    { id: Questions.CustomerService },
    { id: Questions.PaidReview },
    { id: Questions.Email },
  ];

  return (
    <S.Modal
      width={null} // Unset auto width
      onRequestClose={handleClose}
      isOpen={openId === uid}
      key="StoreReviewForm"
    >
      <S.Content height={viewportHeight}>
        <S.FormWrapper height={viewportHeight}>
          <S.Form
            id={FORM_ID}
            onSubmit={handleSubmit}
            onCancel={handleClose}
            onDelete={isEdit ? handleDelete : undefined}
            pending={pending}
            actionType={getActionType()}
            onChange={saveDraft}
          >
            <S.PageTitle>{t('reviews', 'store.form-title')}</S.PageTitle>
            {store ? (
              <StoreInfoCard
                storeId={store.id}
                storeName={store.name}
                featured={store.featured}
                hasLogo={store.hasLogo}
                reviewCount={store.userReviewSummary.countTotal}
                logoUrl={store.logo}
                logoWidth={SHOP_LOGO_WIDTH}
              />
            ) : null}

            {market.shopOfTheYear?.year && (
              <T domain="reviews">store.soty-info.md</T>
            )}

            {questions.map(question => {
              const answer = currentReview?.answers?.find(
                a => a && a.id === question.id
              );
              const showQuestion = currentReview?.answers?.length;

              switch (question.id) {
                case Questions.Rating: {
                  if (isEdit && !currentReview?.rating) return null;

                  return (
                    <div key={question.id}>
                      <Title>{t('reviews', 'store.question-rating')}</Title>
                      <MultiRadio
                        name={Questions.Rating}
                        variant={Variants.rate}
                        options={[1, 2, 3, 4, 5]}
                        optionValues={[
                          t('reviews', 'rating.terrible'),
                          t('reviews', 'rating.bad'),
                          t('reviews', 'rating.ok'),
                          t('reviews', 'rating.good'),
                          t('reviews', 'rating.excellent'),
                        ]}
                        required
                        currentValue={
                          currentReview?.rating
                            ? currentReview?.rating / 2
                            : undefined
                        }
                      />
                    </div>
                  );
                }

                case Questions.Review: {
                  return (
                    <div key={question.id}>
                      <Title>{t('reviews', 'store.question-review')}</Title>
                      <Text
                        type={TextTypes.TextArea}
                        name={Questions.Review}
                        placeholder={t(
                          'reviews',
                          'store.question-review-placeholder'
                        )}
                        defaultValue={currentReview?.reviewText}
                        minLength={20}
                        required
                      />

                      <Info>
                        <T domain="reviews">dialog.rules.md</T>
                      </Info>
                    </div>
                  );
                }

                case Questions.Recommend: {
                  if (isEdit && !showQuestion) return null;

                  return (
                    <div key={question.id}>
                      <Title>
                        {t('reviews', 'store.question-recommendation', {
                          name: store.name,
                        })}
                      </Title>

                      <MultiRadio
                        name={Questions.Recommend}
                        variant={Variants.scale}
                        options={[1, 2, 3, 4, 5]}
                        optionValues={[
                          t('reviews', 'store.answer-recommendation-1'),
                          t('reviews', 'store.answer-recommendation-2'),
                          t('reviews', 'store.answer-recommendation-3'),
                          t('reviews', 'store.answer-recommendation-4'),
                          t('reviews', 'store.answer-recommendation-5'),
                        ]}
                        labels={[
                          t('reviews', 'store.answer-recommendation-1'),
                          '',
                          t('reviews', 'store.answer-recommendation-3'),
                          '',
                          t('reviews', 'store.answer-recommendation-5'),
                        ]}
                        required
                        currentValue={answer?.answer}
                      />
                    </div>
                  );
                }

                case Questions.DeliveryAccuracy: {
                  if (isEdit && !showQuestion) return null;

                  return (
                    <div key={question.id}>
                      <Title>
                        {t('reviews', 'store.question-delivery-time')}
                        <S.Optional>
                          {t('reviews', 'form.question-optional')}
                        </S.Optional>
                      </Title>

                      <MultiRadio
                        name={Questions.DeliveryAccuracy}
                        variant={Variants.scale}
                        options={[1, 2, 3, 4, 5]}
                        optionValues={[
                          t('reviews', 'store.answer-delivery-time-1'),
                          t('reviews', 'store.answer-delivery-time-2'),
                          t('reviews', 'store.answer-delivery-time-3'),
                          t('reviews', 'store.answer-delivery-time-4'),
                          t('reviews', 'store.answer-delivery-time-5'),
                        ]}
                        labels={[
                          t('reviews', 'store.answer-delivery-time-1'),
                          '',
                          t('reviews', 'store.answer-delivery-time-3'),
                          '',
                          t('reviews', 'store.answer-delivery-time-5'),
                        ]}
                        currentValue={answer?.answer}
                      />
                    </div>
                  );
                }

                case Questions.CustomerService: {
                  if (isEdit && !showQuestion) return null;

                  return (
                    <div key={question.id}>
                      <Title>
                        {t('reviews', 'store.question-customer-service')}

                        <S.Optional>
                          {t('reviews', 'form.question-optional')}
                        </S.Optional>
                      </Title>
                      <MultiRadio
                        name={Questions.CustomerService}
                        variant={Variants.scale}
                        options={[1, 2, 3, 4, 5]}
                        optionValues={[
                          t('reviews', 'store.answer-customer-service-1'),
                          t('reviews', 'store.answer-customer-service-2'),
                          t('reviews', 'store.answer-customer-service-3'),
                          t('reviews', 'store.answer-customer-service-4'),
                          t('reviews', 'store.answer-customer-service-5'),
                        ]}
                        labels={[
                          t('reviews', 'store.answer-customer-service-1'),
                          '',
                          t('reviews', 'store.answer-customer-service-3'),
                          '',
                          t('reviews', 'store.answer-customer-service-5'),
                        ]}
                        currentValue={answer?.answer}
                      />
                    </div>
                  );
                }

                case Questions.PaidReview: {
                  if (isEdit && !showQuestion) return null;

                  return (
                    <div key={question.id}>
                      <Title>{t('reviews', 'store.question-sponsored')}</Title>
                      <Info>
                        <T domain="reviews">store.question-sponsored-info.md</T>
                      </Info>
                      <MultiRadio
                        name={Questions.PaidReview}
                        variant={Variants.boolean}
                        options={[0, 1]}
                        required
                        currentValue={answer?.answer}
                      />
                    </div>
                  );
                }

                case Questions.PurchaseDate: {
                  if (!setPurchaseDate) return null;

                  return (
                    <div>
                      <Title>{t('reviews', 'dialog.purchase-date')}</Title>

                      <S.PurchaseDateInput
                        type="date"
                        name="purchaseDate"
                        placeholder={
                          market?.modules?.userReviewPurchaseDate
                            ?.datePlaceholderFormat
                        }
                        defaultValue={currentReview?.purchaseDate || undefined}
                      />
                    </div>
                  );
                }

                case Questions.Email: {
                  if (isEdit || isAuthenticated) return null;

                  return (
                    <div key={question.id}>
                      <Title>{t('reviews', 'dialog.email-label')}</Title>
                      <Text
                        name={Questions.Email}
                        type={TextTypes.Email}
                        placeholder="Email"
                        required
                      />

                      <Info>{t('reviews', 'form.email-usage-policy')}</Info>

                      <Title>
                        {t('reviews', 'form.user-login-promo')}{' '}
                        <S.Link to="/login">
                          {t('account', 'sign-in-promo.sign-in-button')}
                        </S.Link>{' '}
                        {t('reviews', 'form.user-login-promo-connect')}
                      </Title>
                    </div>
                  );
                }

                case Questions.DisplayName: {
                  if (!setDisplayName) return null;

                  return (
                    <div key={question.id}>
                      <Title>{t('reviews', 'dialog.display-name-info')}</Title>
                      <Text
                        name={Questions.DisplayName}
                        type={TextTypes.Text}
                        placeholder={t('reviews', 'dialog.display-name-label')}
                        required
                      />
                    </div>
                  );
                }

                default:
                  return null;
              }
            })}
          </S.Form>
        </S.FormWrapper>
      </S.Content>
    </S.Modal>
  );
};

export default memo(StoreReviewForm);
